import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Calculator, ClipboardCheck, ChevronDown, ExternalLink, Info,
  CheckCircle2, XCircle, AlertTriangle, Gauge, Sparkles, ArrowRight, Pencil,
} from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { StatusBadge } from "@/components/StatusBadge";
import { JobLinks } from "@/components/JobLinks";
import { PROVINCE_COLORS } from "@/lib/constants";
import { PdfButton } from "@/components/PdfButton";
import { LeadCapture } from "@/components/LeadCapture";
import { FeedbackWidget } from "@/components/FeedbackWidget";

const STATUS_ORDER = { eligible: 0, potentially_eligible: 1, not_eligible: 2 };

export default function Results() {
  const { result, setResult, profile } = useProfile();
  const [tab, setTab] = useState("score");

  if (!result) return <EmptyState />;

  const { crs, streams, summary, clb } = result;
  const sorted = [...streams].sort(
    (a, b) => STATUS_ORDER[a.eligibility_status] - STATUS_ORDER[b.eligibility_status]
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Your results</h1>
          <p className="mt-2 text-slate-600">
            Estimated points and eligibility across {summary.total} PNP streams. English CLB level: <strong>{clb.min}</strong>{clb.french > 0 && <> · French NCLC: <strong data-testid="french-clb">{clb.french}</strong></>}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <PdfButton kind="full" result={result} profile={profile} testId="results-pdf-btn" dark />
          <Link to="/assessment" data-testid="edit-profile-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
            <Pencil className="h-4 w-4" /> Edit profile
          </Link>
        </div>
      </div>

      {/* Summary + CRS */}
      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="grid gap-4 sm:grid-cols-3">
            <SummaryTile testId="summary-eligible" icon={CheckCircle2} label="Eligible" value={summary.eligible} color="#166534" bg="#F0FDF4" />
            <SummaryTile testId="summary-potential" icon={AlertTriangle} label="Potentially Eligible" value={summary.potentially_eligible} color="#9A3412" bg="#FFFBEB" />
            <SummaryTile testId="summary-not-eligible" icon={XCircle} label="Not Eligible" value={summary.not_eligible} color="#991B1B" bg="#FEF2F2" />
          </div>
        </div>
        <div className="lg:col-span-4">
          <CrsCard crs={crs} />
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-10">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-testid="results-tabs">
          <TabButton active={tab === "score"} onClick={() => setTab("score")} icon={Calculator} testId="tab-score">
            Score Calculator
          </TabButton>
          <TabButton active={tab === "eligibility"} onClick={() => setTab("eligibility")} icon={ClipboardCheck} testId="tab-eligibility">
            Eligibility Checker
          </TabButton>
        </div>

        <p className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          These are simplified estimates for guidance only. Draw cutoffs change frequently — always confirm with official program sources.
        </p>

        {tab === "score" ? (
          <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {sorted.map((s) => <ScoreCard key={s.id} stream={s} />)}
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            {sorted.map((s) => <EligibilityCard key={s.id} stream={s} />)}
          </div>
        )}
      </div>

      <FeedbackWidget page="results" assessmentId={result.assessment_id} />
      <LeadCapture result={result} onSaved={(email) => setResult({ ...result, lead_email: email })} />

      <JobLinks profile={profile} />

      <div className="mt-10 flex justify-center">
        <Link to="/streams" className="inline-flex items-center gap-2 rounded-md bg-[#0F172A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1E293B]">
          Compare all streams in the matrix <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
        active ? "bg-[#C8102E] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
      }`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

function SummaryTile({ icon: Icon, label, value, color, bg, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: bg, color }}>
          <Icon className="h-5 w-5" />
        </span>
        <span className="font-mono-tech text-3xl font-bold" style={{ color }}>{value}</span>
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-700">{label}</p>
    </div>
  );
}

export function CrsCard({ crs }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-[#0F172A] p-6 text-white shadow-sm" data-testid="crs-card">
      <div className="flex items-center gap-2 text-slate-300">
        <Gauge className="h-5 w-5 text-[#F87171]" />
        <span className="text-sm font-semibold uppercase tracking-wider">CRS estimate</span>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className="font-mono-tech text-5xl font-bold" data-testid="crs-total">{crs.total}</span>
        <span className="mb-1.5 text-sm text-slate-400">/ 1200</span>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-slate-400">{crs.note}</p>
      <button onClick={() => setOpen((o) => !o)} data-testid="crs-toggle"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-200 hover:text-white">
        {open ? "Hide" : "Show"} breakdown <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-3 space-y-2 border-t border-slate-700 pt-3">
          {crs.breakdown.map((b) => (
            <li key={b.label} className="flex items-start justify-between gap-3 text-sm">
              <span className="text-slate-300">{b.label}</span>
              <span className="font-mono-tech font-semibold text-white">{b.points}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScoreCard({ stream }) {
  const [open, setOpen] = useState(false);
  const isCrs = stream.scoring_model === "crs_based";
  const isEligOnly = stream.scoring_model === "eligibility";
  const max = isCrs ? 1200 : stream.max_points || 100;
  const pct = stream.score != null ? Math.min(100, Math.round((stream.score / max) * 100)) : 0;
  const meetsCutoff = stream.cutoff != null && stream.score != null && stream.score >= stream.cutoff;
  const cutoffPct = stream.cutoff != null ? Math.min(100, (stream.cutoff / max) * 100) : null;

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
      data-testid={`score-card-${stream.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white"
            style={{ backgroundColor: PROVINCE_COLORS[stream.province] }}>{stream.province}</span>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-slate-900">{stream.name}</h3>
          <p className="text-xs text-slate-500">{stream.stream_group}</p>
        </div>
        <StatusBadge status={stream.eligibility_status} size="sm" testId={`score-status-${stream.id}`} />
      </div>

      {isEligOnly ? (
        <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
          <Sparkles className="mb-1 h-4 w-4 text-slate-400" />
          Eligibility-based stream — no ranking score is used.
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex items-end justify-between">
            <div>
              <span className="font-mono-tech text-3xl font-bold text-slate-900" data-testid={`score-value-${stream.id}`}>
                {stream.score}
              </span>
              <span className="text-sm text-slate-400"> {isCrs ? "CRS" : `/ ${stream.max_points}`}</span>
            </div>
            {stream.cutoff != null && (
              <span className={`text-xs font-semibold ${meetsCutoff ? "text-emerald-600" : "text-amber-600"}`}>
                cutoff ~{stream.cutoff}
              </span>
            )}
          </div>
          <div className="relative mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meetsCutoff ? "#16A34A" : "#C8102E" }} />
            {cutoffPct != null && (
              <div className="absolute top-0 h-full w-0.5 bg-slate-700" style={{ left: `${cutoffPct}%` }} title="Cutoff" />
            )}
          </div>
          {isCrs && <p className="mt-2 text-xs text-slate-500">Express Entry-aligned — ranked on your CRS score.</p>}
        </div>
      )}

      {stream.breakdown.length > 0 && (
        <>
          <button onClick={() => setOpen((o) => !o)} data-testid={`score-breakdown-toggle-${stream.id}`}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8102E] hover:underline">
            {open ? "Hide" : "View"} point breakdown <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <ul className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
              {stream.breakdown.map((b) => (
                <li key={b.label} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{b.label}</span>
                  <span className="font-mono-tech font-semibold text-slate-900">{b.points}{b.max ? `/${b.max}` : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className="mt-auto flex items-center justify-between pt-4">
        <Link to={`/assessment?stream=${stream.id}`} data-testid={`focus-stream-${stream.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#C8102E] hover:underline">
          Focus on this stream <ArrowRight className="h-3 w-3" />
        </Link>
        <a href={stream.official_url} target="_blank" rel="noreferrer" data-testid={`official-link-${stream.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900">
          Official page <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

export function EligibilityCard({ stream }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`elig-card-${stream.id}`}>
      <div className="flex flex-col justify-between gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-start">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white"
            style={{ backgroundColor: PROVINCE_COLORS[stream.province] }}>{stream.province}</span>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">{stream.name}</h3>
            <p className="text-sm text-slate-500">{stream.province_name} · {stream.stream_group}</p>
          </div>
        </div>
        <StatusBadge status={stream.eligibility_status} testId={`elig-status-${stream.id}`} />
      </div>

      {stream.status_program !== "active" && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600">
          <Info className="h-4 w-4" /> Program status: {stream.status_program} — {stream.status_note}
        </p>
      )}

      <p className="mt-4 text-sm leading-relaxed text-slate-600">{stream.description}</p>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Criteria met</h4>
          <ul className="mt-2 space-y-2">
            {stream.criteria_met.length === 0 && <li className="text-sm text-slate-400">None yet</li>}
            {stream.criteria_met.map((c) => (
              <li key={c.label} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span><strong className="font-semibold">{c.label}.</strong> {c.detail}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-rose-700">Missing / to strengthen</h4>
          <ul className="mt-2 space-y-2">
            {stream.missing.length === 0 && <li className="text-sm text-emerald-600">All criteria satisfied</li>}
            {stream.missing.map((c) => (
              <li key={c.label} className="flex items-start gap-2 text-sm text-slate-700">
                {c.severity === "hard"
                  ? <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                  : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
                <span><strong className="font-semibold">{c.label}.</strong> {c.detail}{c.hint ? ` ${c.hint}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FEF2F2] text-[#C8102E]">
        <ClipboardCheck className="h-8 w-8" />
      </div>
      <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">No assessment yet</h1>
      <p className="mt-2 text-slate-600">Complete the profile questionnaire to see your PNP scores and eligibility.</p>
      <Link to="/assessment" data-testid="empty-start-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#C8102E] px-6 py-3 text-sm font-semibold text-white hover:bg-[#A60C24]">
        Start your assessment <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
